import { useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { SimpleChipItem, Textfield } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { SettingSectionShell } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/items/edit/SettingSectionShell";
import type { ICampaignSettingField } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/types";
import { useBrandDraft } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useBrandDraft";
import { BrandAutocompleteCombobox } from "app/pages/digital-marketing/generative-ai/shared/BrandAutocompleteCombobox/BrandAutocompleteCombobox";

interface IBrandSettingProps extends ICampaignSettingField {
    selectedBrands: string[];
    setSelectedBrands: (brands: string[]) => void;
}

export const BrandsSetting = ({ selectedBrands, setSelectedBrands, isReadOnly }: IBrandSettingProps) => {
    const [emptyListError, setEmptyListError] = useState<string>("");
    const {
        brandFieldRef,
        brandDraft,
        setBrandDraft,
        trimmedBrand,
        brandDraftErrorMessage,
        canAddBrand,
        addBrand,
        commitBrand,
        onBrandKeyDown,
    } = useBrandDraft({
        existingBrands: selectedBrands,
        duplicateErrorKey: "llmTracker.campaignQuery.edit.brands.error.duplicate",
        onAdd: (brand) => {
            setSelectedBrands([...selectedBrands, brand]);
            setEmptyListError("");
        },
    });
    const error = brandDraftErrorMessage || (!trimmedBrand ? emptyListError : "");

    const deleteBrand = (brand: string): void => {
        const newBrandsList = selectedBrands.filter((b) => b !== brand);
        setSelectedBrands(newBrandsList);

        if (!newBrandsList.length) {
            setEmptyListError(translate("llmTracker.campaignQuery.edit.brands.error.empty"));
        }
    };

    const chips = (
        <Flex flexWrap="wrap">
            {selectedBrands.map((brand) => (
                <SimpleChipItem
                    key={brand}
                    text={brand}
                    hideActionButton={isReadOnly}
                    onCloseItem={() => deleteBrand(brand)}
                />
            ))}
        </Flex>
    );

    return isReadOnly ? (
        <Flex
            flexDirection="column"
            gap="12px"
        >
            <Text
                fontVariant="subtitle2"
                color={colorsPalettes.carbon[500]}
            >
                {translate("llmTracker.campaignQuery.edit.brands.title.read-only")}
            </Text>
            {chips}
        </Flex>
    ) : (
        <SettingSectionShell
            title={translate("llmTracker.campaignQuery.edit.brands.title")}
            field={
                <BrandAutocompleteCombobox
                    query={brandDraft}
                    onSelect={commitBrand}
                    showTypedAsOption
                    dataAutomation="llm-edit-brand"
                    excludedBrands={selectedBrands}
                    disabled={!!brandDraftErrorMessage}
                >
                    <Textfield
                        ref={brandFieldRef}
                        onChange={setBrandDraft}
                        placeholder={translate("llmTracker.campaignQuery.edit.brands.placeholder")}
                        onApplyClick={canAddBrand ? addBrand : undefined}
                        onKeyDown={onBrandKeyDown}
                        ApplyButtonText={translate("global.add")}
                        error={!!error}
                        errorMessage={error}
                    />
                </BrandAutocompleteCombobox>
            }
        >
            {chips}
        </SettingSectionShell>
    );
};
